
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { useIsMobile } from '@/hooks/use-mobile';

interface User {
  id: string;
  email: string;
  full_name: string;
  city?: string;
  address_state?: string;
  paid_courses_count: number;
  payment_status?: string;
  total_payment_amount: number;
  has_profile: boolean;
  created_at: string;
  last_sign_in_at?: string;
  phone_number?: string;
}

interface UsersTableProps {
  users: User[];
  isLoading: boolean;
}

export function UsersTable({ users, isLoading }: UsersTableProps) {
  const isMobile = useIsMobile();
  
  // Function to format currency
  const formatCurrency = (amount: number | undefined) => {
    if (amount === undefined) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <div className="border rounded-md">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">
              <Checkbox />
            </TableHead>
            <TableHead>User name</TableHead>
            {!isMobile && <TableHead>Email</TableHead>}
            {!isMobile && <TableHead>Location</TableHead>}
            <TableHead>Courses</TableHead>
            <TableHead className="text-right">Amount</TableHead>
          </TableRow>
        </TableHeader>
        {!isLoading && (
          <TableBody>
            {users.length > 0 ? (
              users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="p-2 md:p-4">
                    <Checkbox />
                  </TableCell>
                  <TableCell className="p-2 md:p-4">
                    <Link 
                      to={`/users/${user.id}`}
                      className="font-medium hover:underline"
                    >
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <span>{user.full_name}</span>
                          {!user.has_profile && (
                            <span className="text-xs px-2 py-0.5 bg-white text-black rounded-full border border-black">
                              Incomplete
                            </span>
                          )}
                        </div>
                        {isMobile && user.email && (
                          <span className="text-xs text-muted-foreground truncate max-w-[120px]">
                            {user.email}
                          </span>
                        )}
                        {isMobile && (user.city || user.address_state) && (
                          <span className="text-xs text-muted-foreground">
                            {user.city && user.address_state 
                              ? `${user.city}, ${user.address_state}` 
                              : user.city || user.address_state}
                          </span>
                        )}
                      </div>
                    </Link>
                  </TableCell>
                  {!isMobile && (
                    <TableCell>
                      {user.email}
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell>
                      {user.city && user.address_state 
                        ? `${user.city}, ${user.address_state}` 
                        : user.city || user.address_state || 'Location not set'}
                    </TableCell>
                  )}
                  <TableCell className="p-2 md:p-4">
                    {user.paid_courses_count} {isMobile ? '' : `course${user.paid_courses_count !== 1 ? 's' : ''}`}
                  </TableCell>
                  <TableCell className="text-right p-2 md:p-4">
                    {formatCurrency(user.total_payment_amount)}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={isMobile ? 4 : 6} className="h-24 text-center">
                  No users found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        )}
      </Table>
      {isLoading && (
        <div className="p-4 text-center text-muted-foreground">
          Loading users...
        </div>
      )}
    </div>
  );
}
